// DataSourceEntriesImportModal — replace one data source's records from a CSV.
//
// The sibling of DataSourceImportModal, and the difference matters: that one
// CREATES a source from a file, so it collects the schema fields; this one fills
// a source that already exists, so it collects nothing but the file and names
// its target by id. Because the columns are already known, the CSV is validated
// against them (validateEntriesCsv) — the header-vs-schema rule the list-page
// import has no way to apply.
//
// Upload is the same three steps for the same reason (the api client encrypts
// request bodies, so a File cannot go through it):
//
//   1. reserve  POST media/upload-public-file  { fileName } → { uploadUrl }
//   2. upload   PUT the raw bytes to uploadUrl, outside axios
//   3. commit   POST import/import-datasource  { dataSourceId, fileName, filePathUrl }
//
// The file is validated the moment it is picked, not on submit, so "Import
// records" only ever enables for a file that will actually import — which is
// what makes its disabled state meaningful.

import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Alert, Button, Icon, Modal } from '@/components/common';
import type { DataSourceColumn } from '@/api/endpoints/datasourceApi';
import { useImportDataSourceEntriesMutation } from '@/api/endpoints/datasourceApi';
import { useUploadPublicFileMutation } from '@/api/endpoints/mediaApi';
import { uploadToPresignedUrl } from '@/utils/uploadFile';
import { downloadTextFile } from '@/utils/downloadUrl';
import { DEV_ONLY } from '@/config/config';
import { parseDataSourceCsv, publicUrlOf, type ParsedDataSourceCsv } from '../shared/parseDataSourceCsv';
import { buildEntriesTemplateCsv, entriesTemplateFileName } from '../shared/dataSourceTemplate';

interface DataSourceEntriesImportModalProps {
  onClose: () => void;
  dataSourceId: string;
  dataSourceName: string;
  columns: DataSourceColumn[];
}

const DataSourceEntriesImportModal = ({
  onClose,
  dataSourceId,
  dataSourceName,
  columns,
}: DataSourceEntriesImportModalProps) => {
  const { t } = useTranslation();
  const [reserveUpload] = useUploadPublicFileMutation();
  const [importEntries] = useImportDataSourceEntriesMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Only ever holds a file that already passed validation.
  const [picked, setPicked] = useState<{ file: File; parsed: ParsedDataSourceCsv } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);

  const columnNames = columns.map((column) => column.columnName);

  const accept = async (file?: File) => {
    if (!file) return;
    const result = await parseDataSourceCsv(file, columnNames);
    if (result.status === 'ok') {
      setPicked({ file, parsed: result.parsed });
      return;
    }
    if (DEV_ONLY && result.cellErrors) console.log('CSV import errors:', result.cellErrors);
    toast.error(t(result.errorKey, result.params));
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Lets the same file be re-picked after a rejection; without it the input's
    // value never changes and onChange never fires again.
    e.target.value = '';
    void accept(file);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    void accept(e.dataTransfer.files?.[0]);
  };

  const downloadTemplate = () =>
    downloadTextFile(buildEntriesTemplateCsv(columns), entriesTemplateFileName(dataSourceName));

  const submit = async () => {
    if (!picked) return;
    const { file } = picked;
    setSaving(true);
    try {
      const { uploadUrl } = await reserveUpload({ fileName: file.name }).unwrap();
      await uploadToPresignedUrl(uploadUrl, file);
      await importEntries({
        dataSourceId,
        fileName: file.name,
        filePathUrl: publicUrlOf(uploadUrl),
      }).unwrap();
      toast.success(t('datasource_entries_import_success'));
      onClose();
    } catch {
      // Stays open so the picked file survives and a retry is one click.
      toast.error(t('error_generic'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      icon='upload'
      title={t('datasource_entries_import_title', { name: dataSourceName })}
      subtitle={t('datasource_entries_import_subtitle')}
      footer={
        <>
          {/* Pushed to the leading edge by the stylesheet, so it flips in RTL. */}
          <Button
            variant='ghost'
            iconStart='file-down'
            className='datasource-import__download'
            onClick={downloadTemplate}
            disabled={saving}
          >
            {t('datasource_entries_import_download')}
          </Button>
          <Button variant='secondary' onClick={onClose} disabled={saving}>
            {t('button_cancel')}
          </Button>
          <Button iconStart='upload' onClick={submit} loading={saving} disabled={!picked}>
            {t('datasource_entries_import_submit')}
          </Button>
        </>
      }
    >
      <div className='datasource-create'>
        {picked ? (
          // Same receipt card the list-page import uses once a file is accepted.
          <div className='datasource-import__file'>
            <span className='datasource-import__file-icon' aria-hidden='true'>
              <Icon name='file-check' size={20} />
            </span>
            <span className='datasource-import__file-text'>
              <span className='datasource-import__file-name'>{picked.file.name}</span>
              <span className='datasource-import__file-meta'>
                {t('datasource_import_records_meta', {
                  records: picked.parsed.recordCount,
                  columns: picked.parsed.columnCount,
                  count: picked.parsed.recordCount,
                })}
              </span>
            </span>
            <Button variant='ghost' size='sm' onClick={() => setPicked(null)} disabled={saving}>
              {t('button_clear')}
            </Button>
          </div>
        ) : (
          <div
            className={[
              'datasource-import__dropzone',
              dragging && 'datasource-import__dropzone--active',
            ]
              .filter(Boolean)
              .join(' ')}
            onDragOver={(e) => {
              // Both handlers must preventDefault or the browser navigates to
              // the dropped file instead of handing it over.
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
          >
            <span className='datasource-import__dropzone-icon' aria-hidden='true'>
              <Icon name='file-spreadsheet' size={20} />
            </span>
            <span className='datasource-import__dropzone-text'>
              <span className='datasource-import__dropzone-title'>
                {t('datasource_entries_import_dropzone')}
              </span>
              <span className='datasource-import__dropzone-hint'>
                {t('datasource_entries_import_dropzone_hint')}
              </span>
            </span>
            <Button
              variant='secondary'
              iconStart='upload'
              onClick={() => fileInputRef.current?.click()}
            >
              {t('datasource_entries_import_browse')}
            </Button>
            <input ref={fileInputRef} type='file' accept='.csv,text/csv' hidden onChange={onPick} />
          </div>
        )}

        {/* Counts COLUMNS, not records: the template ships empty, so promising
            the current records would describe a file we do not generate. */}
        <Alert variant='info'>
          {t('datasource_entries_import_note', { count: columns.length })}
        </Alert>
      </div>
    </Modal>
  );
};

export default DataSourceEntriesImportModal;
